/**
 * Team Workload - which tasks fall in a range.
 *
 * ── What a task covers ──────────────────────────────────────────────────────
 *
 * An open task occupies every day from its start to its due date. Most tasks are given only a due date, so with no start the day it
 * was created stands in. Work past its date and still not handed in stays on the person's plate for every range that reaches today
 * or later - a task overdue since last month must not vanish from "this week".
 *
 * A completed task covers every day from its start up to the day it was completed, and none after.
 */
import { HANDED_OVER, STATUS } from './constants';

/** In review, approved or completed: the work has left the person's hands. */
export const isHandedOver = (task) => HANDED_OVER.includes(task.status);

/**
 * Behind: past its due date and still with the person doing it. Review pending and Approved are not counted - the work has been
 * handed over, and any delay from there belongs to whoever is reviewing it.
 */
export function isOverdue(task, today) {
  return Boolean(task.dueDate) && task.dueDate < today && [STATUS.PENDING, STATUS.IN_PROGRESS, STATUS.ON_HOLD].includes(task.status);
}

/** Undated tasks have no window, so no range can contain them. */
export function isDated(task) {
  return Boolean(task.dueDate);
}

/** Start date, else the day it was created - never later than the due date. */
export function windowStart(task) {
  const start = task.startDate || task.createdOn || task.dueDate;
  return start <= task.dueDate ? start : task.dueDate;
}

/** An open task in the range: its window overlaps it, or it is overdue and the range reaches today. */
export function inRange(task, from, to, today) {
  if (!isDated(task)) return false;
  if (windowStart(task) <= to && task.dueDate >= from) return true;
  return isOverdue(task, today) && to >= today;
}

/**
 * A completed task was being worked on from its start (else the day it was created) up to the day it was completed: it shows in
 * any range that overlaps those days, and in none after it - finished today, it is gone from tomorrow.
 */
export function workedInRange(task, from, to) {
  if (!task.completedOn) return false;
  const start = [task.startDate || task.createdOn || task.completedOn, task.completedOn].sort()[0];
  return start <= to && task.completedOn >= from;
}

/** Whether a task belongs on a person's list for the range: completed ones by the days worked, open ones by their dates. */
export function shownInRange(task, from, to, today) {
  if (task.status === STATUS.COMPLETED) return workedInRange(task, from, to);
  return inRange(task, from, to, today) || !isDated(task);
}

/**
 * The projects anybody on the page holds open (not Completed) work in, inside the range: tasks, people and overdue per project,
 * most overdue first. A task with two assignees on the page counts once for its project.
 */
export function projectsInRange({ people, tasks, from, to, today }) {
  const onPage = new Set(people.map((person) => person.id));
  const out = new Map();

  tasks.forEach((task) => {
    const holders = task.assigneeIds.filter((id) => onPage.has(id));
    // finished work is shown on each person, but the panel counts work still to do
    if (!holders.length || task.status === STATUS.COMPLETED || !inRange(task, from, to, today)) return;

    const project = out.get(task.projectId) || { projectId: task.projectId, name: task.projectName, tasks: 0, overdue: 0, people: new Set() };
    project.tasks += 1;
    if (isOverdue(task, today)) project.overdue += 1;
    holders.forEach((id) => project.people.add(id));
    out.set(task.projectId, project);
  });

  return [...out.values()]
    .map((project) => ({ ...project, people: project.people.size }))
    .sort((a, b) => b.overdue - a.overdue || b.tasks - a.tasks || String(a.name).localeCompare(String(b.name)));
}

/**
 * A person's tasks in the order the drawer lists them: work still with them first (the ones with no hours at the top, then by due
 * date), then Review pending / Approved, then Completed.
 */
export function sortForDrawer(items) {
  const stage = (task) => (task.status === STATUS.COMPLETED ? 2 : isHandedOver(task) ? 1 : 0);
  return [...items].sort(
    (a, b) =>
      stage(a.task) - stage(b.task) ||
      Number(Boolean(a.plan)) - Number(Boolean(b.plan)) ||
      String(a.task.dueDate || '9999').localeCompare(String(b.task.dueDate || '9999')),
  );
}

/** The projects named on the page's tasks, by name - the Project filter's options. */
export function projectOptionsOf(tasks) {
  const seen = new Map();
  tasks.forEach((task) => seen.set(task.projectId, task.projectName));
  return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
}
